import type { FilterRuleModel } from '@/shared/generated'

import { useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
	filter,
	map,
	pipe,
	prop,
	propEq,
	propOr,
	reduce,
	sum,
	values,
} from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useOptimizationQuery } from '@/entities/optimization'
import {
	GROUP_VALUE_COLORS,
	MetricGroup,
	MetricGroupValue,
	useRepricingStatisticQuery,
} from '@/entities/optimization-statistic'

import ProgressRow from '@/shared/components/Progress/ProgressRow'
import { Operators } from '@/shared/components/query-builder'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useMainSourceProperty } from '@/shared/hooks/data'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { ampli } from '@/shared/lib/ampli'
import { formatNumber } from '@/shared/tools/locale'
import { getFiltersQueryString } from '@/shared/tools/location'

export default function ItemsChangedWidget({
	filters,
}: {
	filters: FilterRuleModel[]
}) {
	const { t } = useTranslation()

	const navigate = useNavigate()
	const optimizationGroupId = useOptimizationGroupId()!
	const sourceProperty = useMainSourceProperty()
	const optimizationQuery = useOptimizationQuery(optimizationGroupId)
	const repricingStatisticQuery = useRepricingStatisticQuery(
		{
			optimizations: [
				{
					optimization_id: optimizationQuery.data!.id,
					optimization_group_id: optimizationGroupId,
				},
			],
			filters,
		},

		{
			enabled: !!optimizationGroupId && !!optimizationQuery.data?.id,
			select: pipe(
				prop('metrics'),
				filter(propEq('group_type', MetricGroup.PriceChangeStatus)),

				reduce((acc: any, item) => {
					const groupValue = item.group_value as MetricGroupValue
					if (!acc[groupValue]) {
						acc[groupValue] = {
							color: GROUP_VALUE_COLORS[groupValue] || '',
							groupValue,
						}
					}
					acc[groupValue][item.name] = item.value

					return acc
				}, {} as any)
			),
		}
	)
	const total = sum(
		map(propOr(0, 'product_count'), values(repricingStatisticQuery.data))
	)
	const rows = useMemo(() => {
		const data = repricingStatisticQuery.data
		const { Locked, Increased, Decreased, NotChanged } = MetricGroupValue

		return [
			{
				name: t('status_panel_decreased'),
				products: data?.[Decreased].product_count,
				lines: data?.[Decreased].pricing_line_count,
				color: data?.[Decreased].color,
				filters: [
					{
						name: `diff_${sourceProperty}_pp`,
						operation: Operators.LT as const,
						value: 0,
					},
				],
			},
			{
				name: t('status_panel_increased'),
				products: data?.[Increased].product_count,
				lines: data?.[Increased].pricing_line_count,
				color: data?.[Increased].color,
				filters: [
					{
						name: `diff_${sourceProperty}_pp`,
						operation: Operators.GT as const,
						value: 0,
					},
				],
			},
			{
				name: t('status_panel_price_locked'),
				products: data?.[Locked].product_count,
				lines: data?.[Locked].pricing_line_count,
				color: data?.[Locked].color,
				filters: [
					{
						name: 'is_locked',
						operation: Operators.BL as const,
						value: '1' as const,
					},
				],
			},
			{
				name: t('status_panel_price_not_changed'),
				products: data?.[NotChanged].product_count,
				lines: data?.[NotChanged].pricing_line_count,
				color: data?.[NotChanged].color,
				filters: [
					{
						name: `diff_${sourceProperty}_pp`,
						operation: Operators.EQ as const,
						value: 0,
					},
					{
						name: 'is_locked',
						operation: Operators.BL as const,
						value: '0' as const,
					},
				],
			},
		]
	}, [repricingStatisticQuery.data])
	if (repricingStatisticQuery.isLoading) {
		return <LoadingPlaceholder />
	}
	if (repricingStatisticQuery.isError) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>
					{repricingStatisticQuery.error.message || t('fatal_error_title')}
				</AlertDescription>
			</Alert>
		)
	}
	const handleRowClick = (row: (typeof rows)[number]) => {
		ampli.optSummaryItemsChangedWidgetClickRow()
		navigate({ to: getFiltersQueryString([...filters, ...row.filters]) })
	}

	return (
		<div className='flex w-full flex-col space-y-2'>
			{rows.map((row) => (
				<ProgressRow
					key={row.name}
					onClick={() => handleRowClick(row)}
					barClassName={row.color}
					label={row.name}
					relativeValue={(row.products / total) * 100 || 0}
					absoluteValue={formatNumber(row.products) || 0}
					extraValue={formatNumber(row.lines)}
				/>
			))}
		</div>
	)
}
function LoadingPlaceholder() {
	return (
		<div className='flex flex-col items-center space-y-2'>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-20' />
				<Skeleton className='h-5 w-10' />
			</div>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-24' />
				<Skeleton className='h-5 w-12' />
			</div>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-28' />
				<Skeleton className='h-5 w-12' />
			</div>
		</div>
	)
}
